/**
 * Mock OMEGA log API — used when VITE_API_MODE=mock.
 * Returns 20 seeded entries covering all color-coded entry types.
 */

import type { OmegaLogEntry, OmegaLogTailResponse } from '../types';

function fakeHash(seed: number): string {
  let h = seed >>> 0;
  let out = 'sha256:';
  for (let i = 0; i < 64; i++) {
    h = (h * 1_664_525 + 1_013_904_223) >>> 0;
    out += (h & 0xf).toString(16);
  }
  return out;
}

function fakeSig(seed: number): string {
  let h = seed >>> 0;
  let out = 'ed25519:';
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (let i = 0; i < 88; i++) {
    h = (h * 1_664_525 + 1_013_904_223) >>> 0;
    out += alphabet[h % 64];
  }
  return out + '==';
}

const BASE_NOW = Date.now();
const MIN = 60_000;

const SEEDED_ENTRIES: OmegaLogEntry[] = [
  {
    seq: 0,
    entry_type: 'omega_genesis',
    payload_hash: fakeHash(0),
    payload_json: { founder_pubkey: 'ed25519:4Cpl0/X6KFKwAFUvg5LHm5/nvH71ZiAdhpDiCKCxvts=' },
    signature: fakeSig(0),
  },
  {
    seq: 1,
    entry_type: 'entity',
    payload_hash: fakeHash(1),
    payload_json: { kind: 'OmegaRoot', is_omega: true },
    signature: fakeSig(1),
  },
  {
    seq: 2,
    entry_type: 'constitution_ratification',
    payload_hash: fakeHash(2),
    payload_json: { edition: 1, commandments: 12, signed_at: new Date(BASE_NOW - 29 * MIN).toISOString() },
    signature: fakeSig(2),
  },
  {
    seq: 3,
    entry_type: 'foreseeability_envelope',
    payload_hash: fakeHash(3),
    payload_json: { capabilities: 8, refusal_classes: 8 },
    signature: fakeSig(3),
  },
  {
    seq: 4,
    entry_type: 'founder_key_fingerprint',
    payload_hash: fakeHash(4),
    payload_json: { omega_root_id: 'dc398866-41fd-446c-8a5f-6e5fcd94950a' },
    signature: fakeSig(4),
  },
  {
    seq: 5,
    entry_type: 'brain_binding_updated',
    payload_hash: fakeHash(5),
    payload_json: { brain_id: 'production', specialists: ['market-research', 'product-builder'] },
    signature: fakeSig(5),
  },
  {
    seq: 6,
    entry_type: 'entity',
    payload_hash: fakeHash(6),
    payload_json: { kind: 'FactorySpecialist', id: 'market-research', created_at: new Date(BASE_NOW - 24 * MIN).toISOString() },
    signature: fakeSig(6),
  },
  {
    seq: 7,
    entry_type: 'brain_execution',
    payload_hash: fakeHash(7),
    payload_json: { brain_id: 'production', specialist_id: 'market-research', fired_at: new Date(BASE_NOW - 18 * MIN).toISOString() },
    signature: fakeSig(7),
  },
  {
    seq: 8,
    entry_type: 'trigger_fired',
    payload_hash: fakeHash(8),
    payload_json: { trigger_id: 'aaaaaaaa-0000-0000-0000-000000000001', specialist_id: 'market-research', fired_at: new Date(BASE_NOW - 15 * MIN).toISOString() },
    signature: fakeSig(8),
  },
  {
    seq: 9,
    entry_type: 'attestation',
    payload_hash: fakeHash(9),
    payload_json: { root_id: '11111111-cafe-0000-0000-000000000001', created_at: new Date(BASE_NOW - 12 * MIN).toISOString() },
    signature: fakeSig(9),
  },
  {
    seq: 10,
    entry_type: 'consent',
    payload_hash: fakeHash(10),
    payload_json: { user_id: 'u-001', purpose: 'market_analysis', granted_at: new Date(BASE_NOW - 10 * MIN).toISOString() },
    signature: fakeSig(10),
  },
  {
    seq: 11,
    entry_type: 'decision_pending',
    payload_hash: fakeHash(11),
    payload_json: { decision_id: 'd-001', question: 'Expand to EU market?', recommendation: 'approve', created_at: new Date(BASE_NOW - 8 * MIN).toISOString() },
    signature: fakeSig(11),
  },
  {
    seq: 12,
    entry_type: 'decision_acted',
    payload_hash: fakeHash(12),
    payload_json: { decision_id: 'd-001', action: 'approved', acted_at: new Date(BASE_NOW - 7 * MIN).toISOString() },
    signature: fakeSig(12),
  },
  {
    seq: 13,
    entry_type: 'constitution_amendment',
    payload_hash: fakeHash(13),
    payload_json: { amendment_seq: 1, summary: 'Expand foreseeability clause', signed_at: new Date(BASE_NOW - 5 * MIN).toISOString() },
    signature: fakeSig(13),
  },
  {
    seq: 14,
    entry_type: 'brain_execution',
    payload_hash: fakeHash(14),
    payload_json: { brain_id: 'analytics', specialist_id: 'anomaly-detector', fired_at: new Date(BASE_NOW - 3 * MIN).toISOString() },
    signature: fakeSig(14),
  },
  {
    seq: 15,
    entry_type: 'trigger_fired',
    payload_hash: fakeHash(15),
    payload_json: { trigger_id: 'bbbbbbbb-0000-0000-0000-000000000001', specialist_id: 'anomaly-detector', fired_at: new Date(BASE_NOW - 2 * MIN).toISOString() },
    signature: fakeSig(15),
  },
  {
    seq: 16,
    entry_type: 'revocation',
    payload_hash: fakeHash(16),
    payload_json: { consent_id: 'c-001', revoked_at: new Date(BASE_NOW - MIN).toISOString() },
    signature: fakeSig(16),
  },
  {
    seq: 17,
    entry_type: 'entity',
    payload_hash: fakeHash(17),
    payload_json: { kind: 'ProductDraft', slug: 'cm-tokenized-bond', created_at: new Date(BASE_NOW - 45_000).toISOString() },
    signature: fakeSig(17),
  },
  {
    seq: 18,
    entry_type: 'brain_execution',
    payload_hash: fakeHash(18),
    payload_json: { brain_id: 'discovery', specialist_id: 'pattern-scanner', fired_at: new Date(BASE_NOW - 20_000).toISOString() },
    signature: fakeSig(18),
  },
  {
    seq: 19,
    entry_type: 'trigger_fired',
    payload_hash: fakeHash(19),
    payload_json: { trigger_id: 'cccccccc-0000-0000-0000-000000000001', specialist_id: 'pattern-scanner', fired_at: new Date(BASE_NOW - 5_000).toISOString() },
    signature: fakeSig(19),
  },
];

export async function getOmegaLogTail(
  sinceSeq?: number,
  limit = 50,
): Promise<OmegaLogTailResponse> {
  const cap = Math.min(limit, 200);
  const total = SEEDED_ENTRIES.length;
  const since = sinceSeq ?? Math.max(0, total - cap);
  const slice = SEEDED_ENTRIES.filter((e) => e.seq >= since).slice(0, cap);
  return {
    entries: slice,
    next_seq: slice.length > 0 ? (slice[slice.length - 1]!.seq + 1) : since,
    total,
  };
}
